// Plain-SVG charts for the multi-year training overview (no chart library —
// see CLAUDE.md: packages using optional chaining break the CRA build).

const W = 640;
const M = { l: 34, r: 8, t: 10, b: 22 };

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  const m = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return m * p;
}

const fmtTick = (v: number) => (Math.abs(v) >= 100 || Number.isInteger(v) ? String(Math.round(v)) : v.toFixed(1));

export function Legend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div className="hist__legend">
      {items.map((it) => (
        <span key={it.label} className="hist__legend-item">
          <i style={{ background: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

interface StackedBarsProps {
  cols: { tip: string; parts: number[] }[];
  colors: string[];
  xLabels: (string | null)[];
  height?: number;
  fixedMax?: number;
  unit?: string;
  line?: number[];
  lineColor?: string;
}

export function StackedBars({ cols, colors, xLabels, height = 190, fixedMax, unit = "", line, lineColor = "#1a1a1a" }: StackedBarsProps) {
  const plotW = W - M.l - M.r;
  const plotH = height - M.t - M.b;
  const totals = cols.map((c) => c.parts.reduce((a, b) => a + b, 0));
  const yMax = fixedMax ?? niceMax(Math.max(...totals, ...(line ?? []), 0));
  const y = (v: number) => M.t + plotH - (v / yMax) * plotH;
  const slot = plotW / Math.max(cols.length, 1);
  const bw = Math.max(1, slot - (cols.length > 60 ? 0.8 : 4));

  return (
    <svg viewBox={`0 0 ${W} ${height}`} className="hist__svg" role="img">
      {[0, 1, 2, 3, 4].map((k) => {
        const v = (yMax * k) / 4;
        return (
          <g key={k}>
            <line x1={M.l} x2={W - M.r} y1={y(v)} y2={y(v)} stroke="rgba(26,26,26,0.08)" strokeWidth="1" />
            <text x={M.l - 4} y={y(v) + 3} textAnchor="end" fontSize="9" fill="#9a958a">{fmtTick(v)}{k === 4 ? unit : ""}</text>
          </g>
        );
      })}
      {cols.map((c, i) => {
        let acc = 0;
        return (
          <g key={i}>
            <title>{c.tip}</title>
            {c.parts.map((p, pi) => {
              if (p <= 0) return null;
              const y0 = y(acc);
              acc += p;
              const y1 = y(acc);
              return <rect key={pi} x={M.l + i * slot + (slot - bw) / 2} y={y1} width={bw} height={Math.max(0, y0 - y1)} fill={colors[pi]} />;
            })}
          </g>
        );
      })}
      {line && line.length > 1 && (
        <polyline
          points={line.map((v, i) => `${(M.l + i * slot + slot / 2).toFixed(1)},${y(v).toFixed(1)}`).join(" ")}
          fill="none" stroke={lineColor} strokeWidth="1.6" strokeLinejoin="round" opacity="0.9"
        />
      )}
      {xLabels.map((l, i) => l && (
        <text key={i} x={M.l + i * slot + slot / 2} y={height - 6} textAnchor="middle" fontSize="9" fill="#6b665b">{l}</text>
      ))}
    </svg>
  );
}

interface LineSeries {
  label: string;
  color: string;
  values: (number | null)[];
  dashed?: boolean;
}

interface LineChartProps {
  series: LineSeries[];
  xLabels: (string | null)[];
  tips?: string[];
  height?: number;
  zero?: boolean;   // force y axis to start at 0
  dots?: boolean;
  unit?: string;
}

export function LineChart({ series, xLabels, tips, height = 170, zero = false, dots = false, unit = "" }: LineChartProps) {
  const all = series.flatMap((s) => s.values).filter((v): v is number => v != null);
  if (all.length < 2) return <p className="hist__nodata">Ingen data.</p>;

  let lo = Math.min(...all);
  let hi = Math.max(...all);
  if (zero) lo = Math.min(0, lo);
  const pad = (hi - lo || 1) * 0.08;
  hi += pad;
  if (!zero) lo -= pad;

  const plotW = W - M.l - M.r;
  const plotH = height - M.t - M.b;
  const n = Math.max(...series.map((s) => s.values.length), 2);
  const x = (i: number) => M.l + (i / (n - 1)) * plotW;
  const y = (v: number) => M.t + plotH - ((v - lo) / (hi - lo)) * plotH;

  const pathFor = (values: (number | null)[]) => {
    let d = "";
    let pen = false;
    values.forEach((v, i) => {
      if (v == null) { pen = false; return; }
      d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
      pen = true;
    });
    return d;
  };

  return (
    <svg viewBox={`0 0 ${W} ${height}`} className="hist__svg" role="img">
      {[0, 1, 2, 3].map((k) => {
        const v = lo + ((hi - lo) * k) / 3;
        return (
          <g key={k}>
            <line x1={M.l} x2={W - M.r} y1={y(v)} y2={y(v)} stroke="rgba(26,26,26,0.08)" strokeWidth="1" />
            <text x={M.l - 4} y={y(v) + 3} textAnchor="end" fontSize="9" fill="#9a958a">{fmtTick(v)}{k === 3 ? unit : ""}</text>
          </g>
        );
      })}
      {series.map((s) => (
        <g key={s.label}>
          <path d={pathFor(s.values)} fill="none" stroke={s.color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={s.dashed ? "4 3" : undefined} />
          {dots && s.values.map((v, i) => v != null && (
            <circle key={i} cx={x(i)} cy={y(v)} r="2.6" fill={s.color}>
              <title>{`${tips?.[i] ?? xLabels[i] ?? ""} · ${s.label}: ${fmtTick(v)}${unit}`}</title>
            </circle>
          ))}
        </g>
      ))}
      {xLabels.map((l, i) => l && (
        <text key={i} x={x(i)} y={height - 6} textAnchor="middle" fontSize="9" fill="#6b665b">{l}</text>
      ))}
    </svg>
  );
}
